import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Form } from '@unform/web';

import api from '../../services/api';
import ApolloAuth from '../../services/apolloAuth';
import DocumentFields from '../../components/DocumentFields';
import Notification from '../../components/Notification';
import Button from '../../components/Button';
import Input from '../../components/Input';

import { Container, Text, DocumentFieldsContainer } from './styles';

const Register = () => {
  const formRef = useRef(null);
  const history = useHistory();
  const query = new URLSearchParams(useLocation().search);

  const [isLoading, setLoading] = useState(false);
  // Separado de isLoading: aquele cobre a checagem do token de indicação na
  // montagem, que bloqueia a página inteira; este é só o envio do cadastro.
  const [isSubmitting, setSubmitting] = useState(false);
  // Só visual: não vai no formulário. Quem marca estrangeiro é a ausência de CPF.
  const [isForeigner, setForeigner] = useState(false);

  useEffect(() => {
    (async () => {
      const token = query.get('t');
      if (!token) return;

      setLoading(true);
      try {
        await api.get(`/indication/${token}`);
      } catch (err) {
        if (err.response) {
          Notification({
            message: err.response.data.error,
            type: Notification.types.ERROR,
          });
        } else {
          Notification({
            message: 'Token inválido',
            type: Notification.types.ERROR,
          });
        }

        history.push('/');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /**
   * O Apolo devolve as falhas de validação em `issues`, com o caminho do campo.
   * O unform espera um objeto campo -> mensagem.
   */
  function applyIssues(issues) {
    const errors = issues.reduce(
      (acc, issue) => ({ ...acc, [issue.path]: issue.message }),
      {}
    );

    formRef.current.setErrors(errors);
  }

  function handleSubmitError(err) {
    if (!err.response) {
      Notification({
        message: 'Erro ao realizar cadastro',
        type: Notification.types.ERROR,
      });
      return;
    }

    const { issues, error } = err.response.data;

    if (issues?.length) {
      applyIssues(issues);
      return;
    }

    Notification({
      message: error || 'Erro ao realizar cadastro',
      type: Notification.types.ERROR,
    });
  }

  async function handleSubmitForm(data) {
    if (data.password !== data.confirmPassword) {
      formRef.current.setErrors({
        confirmPassword: 'As senhas não conferem',
      });
      return;
    }

    try {
      setSubmitting(true);

      // O cadastro vai direto para o Apolo. Indicação e tipo de usuário não
      // são assunto dele: viajam no metadata e quem resolve é a API do portal,
      // ao consumir o evento.
      await ApolloAuth.register({
        name: data.name,
        email: data.email,
        // Um documento ou o outro: mandar os dois é recusado pelo Apolo.
        ...(isForeigner
          ? {
            passportNumber: data.passportNumber,
            passportCountry: data.passportCountry,
          }
          : { cpf: data.cpf }),
        password: data.password,
        metadata: {
          indicationToken: query.get('t') || undefined,
          userType: 'Cutter',
        },
      });

      Notification({
        message:
          'Cadastro realizado! Confirme seu e-mail pelo link que enviamos.',
        type: Notification.types.SUCCESS,
      });
      history.push('/');
    } catch (err) {
      handleSubmitError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {isLoading && <div className="loader" />}
      <Container>
        <Text>Seja bem vindo ao portal Due It!</Text>
        <Text>Faça seu cadastro.</Text>
        <Form ref={formRef} onSubmit={handleSubmitForm}>
          <Input type="email" name="email" label="E-mail" />
          <Input type="text" name="name" label="Nome" />
          <DocumentFieldsContainer>
            <DocumentFields isForeigner={isForeigner} onToggle={setForeigner} />
          </DocumentFieldsContainer>
          <Input
            type="password"
            name="password"
            label="Senha"
            withPasswordToggle
          />
          <Input
            type="password"
            name="confirmPassword"
            label="Confirmar senha"
            withPasswordToggle
          />
          <Button
            width={100}
            primary
            text="Cadastrar"
            loadingText="Cadastrando..."
            isLoading={isSubmitting}
            type="submit"
          />
        </Form>
      </Container>
    </>
  );
};

export default Register;
